import {
    extractHttpErrorMessage,
    normalizeNodeFromBackend,
    normalizeSpaceFromBackend,
    parseSpaceCanvasViewportFromUnknown,
    toBackendItemBody,
    toBackendNodeBody,
    toBackendUpdateBody,
} from '@/spaces/helpers/spaces-backend';
import type {
    ActivityLogEntry,
    EnrichDescriptionPayload,
    EnrichDescriptionResponse,
    SimilarSpace,
    Space,
    SpaceCanvasViewport,
    SpaceNode,
    SpaceNodePayload,
    SpacePayload,
} from '@/spaces/types/types';
import { http } from '@pendo/http';
import { getSpacesApiBase, getSpacesHttpConfig } from './spaces-api-config';

/**
 * Re-throws failed HTTP calls with a string message from the server or client.
 *
 * @param err - Rejection from @pendo/http
 */
function throwMappedHttpError (err: unknown): never {
    throw new Error(extractHttpErrorMessage(err));
}

/** Merges `items` from GET or POST create into `nodes`. */
function normalizeFetchedSpace (raw: Record<string, unknown>): Space {
    const payload = { ...raw };
    if (Array.isArray(payload.items)) {
        payload.nodes = payload.items;
    }
    delete payload.items;

    return normalizeSpaceFromBackend(payload);
}

/**
 * Lists all spaces for the current subscription (GET spaces root).
 *
 * @returns Normalized spaces for the signed-in tenant
 */
export function fetchSpaces (): Promise<Space[]> {
    return http
        .get(getSpacesApiBase(), getSpacesHttpConfig())
        .then((res) =>
            (res.data as unknown[]).map((item) =>
                normalizeSpaceFromBackend(item as Record<string, unknown>),
            ),
        )
        .catch(throwMappedHttpError);
}

/**
 * Loads one space by id (GET `.../spaces/:id`).
 *
 * @param id - Space document id
 * @param options - Pass `{ includeItems: true }` for one round-trip space + items (canvas/backlog).
 */
export function fetchSpaceById (
    id: string,
    options?: { includeItems?: boolean },
): Promise<Space> {
    const includeItems = options?.includeItems === true;

    return http
        .get(`${getSpacesApiBase()}/${encodeURIComponent(id)}`, {
            ...getSpacesHttpConfig(),
            ...(includeItems ? { params: { includeItems: 'true' } } : {}),
        })
        .then((res) => normalizeFetchedSpace(res.data as Record<string, unknown>))
        .catch(throwMappedHttpError);
}

/**
 * Fetches a single item from a space (GET `.../spaces/:spaceId/items/:itemId`).
 *
 * @param spaceId - Space document id
 * @param itemId - Item id
 */
export function fetchSpaceItem (spaceId: string, itemId: string): Promise<SpaceNode> {
    return http
        .get(
            `${getSpacesApiBase()}/${encodeURIComponent(spaceId)}/items/${encodeURIComponent(itemId)}`,
            getSpacesHttpConfig(),
        )
        .then((res) => normalizeNodeFromBackend(res.data as Record<string, unknown>))
        .catch(throwMappedHttpError);
}

/**
 * Lists all items for a space (GET `.../spaces/:id/items`).
 *
 * @param id - Space document id
 */
export function fetchSpaceItems (id: string): Promise<SpaceNode[]> {
    return http
        .get(`${getSpacesApiBase()}/${encodeURIComponent(id)}/items`, getSpacesHttpConfig())
        .then((res) =>
            (res.data as unknown[]).map((item) =>
                normalizeNodeFromBackend(item as Record<string, unknown>),
            ),
        )
        .catch(throwMappedHttpError);
}

export interface ActivityLogResponse {
    entries: ActivityLogEntry[];
    hasMore: boolean;
    nextCursor?: string;
}

/**
 * Fetches the activity log for a space (GET `.../spaces/:id/activityLog`).
 *
 * @param spaceId - Space document id
 * @param cursor - Pagination cursor from a previous response's `nextCursor`
 * @returns Paginated activity log entries (newest-to-oldest)
 */
export function fetchSpaceActivityLog (spaceId: string, cursor?: string): Promise<ActivityLogResponse> {
    const params: Record<string, string> = { pageSize: '10' };
    if (cursor) {
        params.cursor = cursor;
    }

    return http
        .get(`${getSpacesApiBase()}/${encodeURIComponent(spaceId)}/activityLog`, {
            ...getSpacesHttpConfig(),
            params,
        })
        .then((res) => res.data as ActivityLogResponse)
        .catch(throwMappedHttpError);
}

/**
 * Creates a space (POST spaces root). Requires auth headers on the backend.
 *
 * @param space - Initial name, description, visibility, and optional nodes
 */
export function createSpace (space: SpacePayload): Promise<Space> {
    const body: Record<string, unknown> = {
        name: space.name,
        description: space.description ?? '',
        visibility: space.visibility,
    };
    if (space.primaryTarget) {
        body.primaryTarget = space.primaryTarget;
    }
    if (space.purpose) {
        body.purpose = space.purpose;
    }
    if (space.signals?.length) {
        body.signals = space.signals;
    }
    if (space.items?.length) {
        body.items = space.items.map((item) => toBackendItemBody(item));
    }

    return http
        .post(getSpacesApiBase(), body, getSpacesHttpConfig())
        .then((res) => normalizeFetchedSpace(res.data as Record<string, unknown>))
        .catch(throwMappedHttpError);
}

/**
 * Enriches a short description using AI (POST `.../spaces/enrich-description`).
 *
 * @param payload - Name and short description to enrich
 * @returns Suggested primaryTarget and purpose
 */
export function enrichDescription (payload: EnrichDescriptionPayload): Promise<EnrichDescriptionResponse> {
    return http
        .post(`${getSpacesApiBase()}/enrich-description`, payload, getSpacesHttpConfig())
        .then((res) => res.data as EnrichDescriptionResponse)
        .catch(throwMappedHttpError);
}

/**
 * Patches a space (PUT `.../spaces/:id`). Only sends fields present in `updates`.
 *
 * @param id - Space id
 * @param updates - Partial space fields (no `edges` sent to the Node service)
 */
export function updateSpace (id: string, updates: Partial<SpacePayload>): Promise<Space> {
    const body = toBackendUpdateBody(updates);

    return http
        .put(`${getSpacesApiBase()}/${encodeURIComponent(id)}`, body, getSpacesHttpConfig())
        .then((res) => normalizeSpaceFromBackend(res.data as Record<string, unknown>))
        .catch(throwMappedHttpError);
}

/**
 * Uploads a custom PNG thumbnail to storage and updates the space (PUT `.../spaces/:id/thumbnail`).
 * Default canvas previews are generated by the backend; use this when the user supplies their own image.
 *
 * @param imageBase64 - Raw PNG base64 (no `data:image/...;base64,` prefix)
 */
export function uploadSpaceThumbnail (spaceId: string, imageBase64: string): Promise<Space> {
    return http
        .put(
            `${getSpacesApiBase()}/${encodeURIComponent(spaceId)}/thumbnail`,
            { imageBase64 },
            getSpacesHttpConfig(),
        )
        .then((res) => normalizeSpaceFromBackend(res.data as Record<string, unknown>))
        .catch(throwMappedHttpError);
}

/**
 * Star or unstar a space for the current user (PUT `.../spaces/:id/star`).
 *
 * @param starred - `true` to star, `false` to remove the star
 */
export function setSpaceStarred (spaceId: string, starred: boolean): Promise<Space> {
    return http
        .put(
            `${getSpacesApiBase()}/${encodeURIComponent(spaceId)}/star`,
            { starred },
            getSpacesHttpConfig(),
        )
        .then((res) => normalizeSpaceFromBackend(res.data as Record<string, unknown>))
        .catch(throwMappedHttpError);
}

/**
 * Persists pan/zoom for the current user (PUT `.../spaces/:id/canvas-view`). Allowed for any user
 * who can open the space, including read-only collaborators.
 */
export function saveSpaceCanvasView (spaceId: string, viewport: SpaceCanvasViewport): Promise<SpaceCanvasViewport> {
    return http
        .put(
            `${getSpacesApiBase()}/${encodeURIComponent(spaceId)}/canvas-view`,
            viewport,
            getSpacesHttpConfig(),
        )
        .then((res) => {
            const parsed = parseSpaceCanvasViewportFromUnknown(res.data);

            if (!parsed) {
                throw new Error('Invalid canvas view response');
            }

            return parsed;
        })
        .catch(throwMappedHttpError);
}

/**
 * Appends a node to a space (POST `.../spaces/:id/items`). Response is the created item.
 *
 * @param spaceId - Target space id
 * @param node - Node fields; optional `id` if the client should keep a stable id
 */
export function addNodeToSpace (
    spaceId: string,
    node: SpaceNodePayload & { id?: string },
): Promise<SpaceNode> {
    const body = toBackendNodeBody(node);

    return http
        .post(`${getSpacesApiBase()}/${encodeURIComponent(spaceId)}/items`, body, getSpacesHttpConfig())
        .then((res) => normalizeNodeFromBackend(res.data as Record<string, unknown>))
        .catch(throwMappedHttpError);
}

/**
 * Updates one space item (PATCH `.../spaces/:spaceId/items/:itemId`).
 *
 * @param spaceId - Parent space id
 * @param itemId - Item id
 * @param updates - Partial item fields to persist
 */
export function updateSpaceItem (
    spaceId: string,
    itemId: string,
    updates: Partial<
        Pick<SpaceNodePayload, 'isBacklog' | 'x' | 'y' | 'width' | 'height' | 'parentItemId' | 'data' | 'name'>
    >,
): Promise<SpaceNode> {
    return http
        .patch(
            `${getSpacesApiBase()}/${encodeURIComponent(spaceId)}/items/${encodeURIComponent(itemId)}`,
            updates,
            getSpacesHttpConfig(),
        )
        .then((res) => normalizeNodeFromBackend(res.data as Record<string, unknown>))
        .catch(throwMappedHttpError);
}

/**
 * Triggers AI summary generation for a space (POST `.../spaces/:id/summarize`).
 *
 * @param id - Space id
 * @returns The updated space with a new summary
 */
export function summarizeSpace (id: string): Promise<Space> {
    return http
        .post(
            `${getSpacesApiBase()}/${encodeURIComponent(id)}/summarize`,
            {},
            getSpacesHttpConfig(),
        )
        .then((res) => normalizeSpaceFromBackend(res.data as Record<string, unknown>))
        .catch(throwMappedHttpError);
}

/**
 * Finds semantically similar spaces via summary-embedding comparison
 * (GET `.../spaces/:id/similar`). Requires the space to already have a summary.
 */
export function fetchSimilarSpaces (id: string): Promise<SimilarSpace[]> {
    return http
        .get(`${getSpacesApiBase()}/${encodeURIComponent(id)}/similar`, getSpacesHttpConfig())
        .then((res) => (res.data as SimilarSpace[]) ?? [])
        .catch(throwMappedHttpError);
}

/**
 * Triggers AI evidence assessment for a space (POST `.../spaces/:id/assess`).
 *
 * @param id - Space id
 * @returns The updated space with a new assessment
 */
export function assessSpace (id: string): Promise<Space> {
    return http
        .post(
            `${getSpacesApiBase()}/${encodeURIComponent(id)}/assess`,
            {},
            getSpacesHttpConfig(),
        )
        .then((res) => normalizeSpaceFromBackend(res.data as Record<string, unknown>))
        .catch(throwMappedHttpError);
}

/**
 * Deletes one item from a space (DELETE `.../spaces/:spaceId/items/:itemId`).
 *
 * @param spaceId - Parent space id
 * @param itemId - Item id to delete
 */
export function deleteSpaceItem (spaceId: string, itemId: string): Promise<void> {
    return http
        .delete(
            `${getSpacesApiBase()}/${encodeURIComponent(spaceId)}/items/${encodeURIComponent(itemId)}`,
            getSpacesHttpConfig(),
        )
        .then(() => undefined)
        .catch(throwMappedHttpError);
}

/**
 * Deletes a space (DELETE `.../spaces/:id`).
 *
 * @param id - Space id
 */
export function deleteSpace (id: string): Promise<void> {
    return http
        .delete(`${getSpacesApiBase()}/${encodeURIComponent(id)}`, getSpacesHttpConfig())
        .then(() => undefined)
        .catch(throwMappedHttpError);
}

/**
 * Fetches the stored image for a space item (GET `.../spaces/:spaceId/items/:itemId/image`).
 *
 * @param spaceId - Parent space id
 * @param itemId - Item id whose image to retrieve
 * @returns Object URL (`blob:…`) suitable for `<img src>` — caller must `URL.revokeObjectURL` when done
 */
export function fetchSpaceItemImage (spaceId: string, itemId: string): Promise<string> {
    return http
        .get(
            `${getSpacesApiBase()}/${encodeURIComponent(spaceId)}/items/${encodeURIComponent(itemId)}/image`,
            { ...getSpacesHttpConfig(), responseType: 'blob' },
        )
        .then((res) => URL.createObjectURL(res.data as Blob))
        .catch(throwMappedHttpError);
}

/**
 * Deletes every space returned by {@link fetchSpaces} (no bulk endpoint on the server).
 */
export async function clearAllSpaces (): Promise<void> {
    const spaces = await fetchSpaces();

    await Promise.all(spaces.map((s) => deleteSpace(s.id)));
}
